/**
 * Fixture song for repertoire markings: three lines of a piano piece carrying the
 * markings the repertoire scene must draw, one MusicXML document per line (one part,
 * two staves, as the line splitter writes it). Two bars a line:
 *
 *   line 1  finger numbers in both hands, on single notes and chords (RH above,
 *           LH below); p and f between the staves, mf below the left hand; a
 *           crescendo hairpin; a pedal bracket
 *   line 2  staccato, accent, tenuto, staccato + accent, strong accent,
 *           staccatissimo; fermatas above and below; sfz; italic "dolce" and "rit.";
 *           a diminuendo hairpin; a pedal bracket
 *   line 3  finger numbers on ledger-line notes above and below the staves; ff, pp;
 *           "cresc. molto" and "calando" (a line that opens with no tempo mark and a
 *           gradual tempo word: as written, our OSMD cannot load it)
 *
 * The line splitter drops hairpins and pedal marks today and keeps one finger number
 * per chord; this fixture keeps them, to show how the scene draws them when present.
 */

type FingerPlacement = 'above' | 'below';

type FixtureChord = {
  pitches: string[];
  /** In divisions (2 divisions = 1 quarter). */
  duration: number;
  staff: 1 | 2;
  fingers?: string[];
  fingerPlacement?: FingerPlacement;
  /** Notation elements on the chord's first note, e.g. '<articulations><staccato/></articulations>'. */
  notations?: string;
};

const NOTE_TYPES: Record<number, string> = { 1: 'eighth', 2: 'quarter', 4: 'half', 8: 'whole' };

function pitchXml(pitch: string): string {
  const step = pitch[0];
  const octave = pitch[pitch.length - 1];
  const accidental = pitch.slice(1, -1);
  const alter =
    accidental === '#' ? '<alter>1</alter>' : accidental === 'b' ? '<alter>-1</alter>' : '';
  return `<pitch><step>${step}</step>${alter}<octave>${octave}</octave></pitch>`;
}

function chordXml(chord: FixtureChord): string {
  const voice = chord.staff === 1 ? 1 : 5;
  return chord.pitches
    .map((pitch, index) => {
      const finger = chord.fingers?.[index];
      const fingerXml = finger
        ? `<technical><fingering placement="${chord.fingerPlacement ?? 'above'}">${finger}</fingering></technical>`
        : '';
      const notations = fingerXml + (index === 0 ? (chord.notations ?? '') : '');
      return [
        '<note>',
        index > 0 ? '<chord/>' : '',
        pitchXml(pitch),
        `<duration>${chord.duration}</duration><voice>${voice}</voice>`,
        `<type>${NOTE_TYPES[chord.duration]}</type><staff>${chord.staff}</staff>`,
        notations ? `<notations>${notations}</notations>` : '',
        '</note>',
      ].join('');
    })
    .join('');
}

const direction = (inner: string, staff: 1 | 2, placement: FingerPlacement): string =>
  `<direction placement="${placement}"><direction-type>${inner}</direction-type><staff>${staff}</staff></direction>`;
const dynamics = (mark: string): string => `<dynamics><${mark}/></dynamics>`;
const words = (text: string): string => `<words font-style="italic">${text}</words>`;
const wedge = (type: 'crescendo' | 'diminuendo' | 'stop'): string => `<wedge type="${type}"/>`;
const pedal = (type: 'start' | 'stop'): string =>
  type === 'start'
    ? '<pedal type="start" line="yes" sign="yes"/>'
    : '<pedal type="stop" line="yes"/>';
const articulations = (...marks: string[]): string =>
  `<articulations>${marks.map((mark) => `<${mark}/>`).join('')}</articulations>`;
const FERMATA_ABOVE = '<fermata type="upright"/>';
const FERMATA_BELOW = '<fermata type="inverted"/>';
const BACKUP_A_BAR = '<backup><duration>8</duration></backup>';

const ATTRIBUTES =
  '<attributes><divisions>2</divisions><key><fifths>0</fifths></key>' +
  '<time><beats>4</beats><beat-type>4</beat-type></time><staves>2</staves>' +
  '<clef number="1"><sign>G</sign><line>2</line></clef>' +
  '<clef number="2"><sign>F</sign><line>4</line></clef>' +
  '</attributes>';

const fingered = (
  pitches: string[],
  duration: number,
  staff: 1 | 2,
  fingers: string[],
  notations?: string,
): FixtureChord => ({
  pitches,
  duration,
  staff,
  fingers,
  fingerPlacement: staff === 1 ? 'above' : 'below',
  notations,
});

const plain = (
  pitches: string[],
  duration: number,
  staff: 1 | 2,
  notations?: string,
): FixtureChord => ({ pitches, duration, staff, notations });

function lineXml(firstMeasureNumber: number, measures: string[][]): string {
  const measureXml = measures.map(
    (parts, index) =>
      `<measure number="${firstMeasureNumber + index}">` +
      (index === 0 ? `<print new-system="yes"/>${ATTRIBUTES}` : '') +
      parts.join('') +
      '</measure>',
  );
  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<score-partwise version="3.1"><part-list>',
    '<score-part id="P1"><part-name>Piano</part-name></score-part></part-list>',
    `<part id="P1">${measureXml.join('')}</part>`,
    '</score-partwise>',
  ].join('');
}

const singleFingered = (notes: Array<[string, string]>, duration: number, staff: 1 | 2) =>
  notes.map(([pitch, finger]) => chordXml(fingered([pitch], duration, staff, [finger])));

const LINE_1 = lineXml(1, [
  [
    direction(dynamics('p'), 1, 'below'),
    ...singleFingered(
      [
        ['C4', '1'],
        ['D4', '2'],
        ['E4', '3'],
        ['G4', '5'],
      ],
      2,
      1,
    ),
    BACKUP_A_BAR,
    direction(pedal('start'), 2, 'below'),
    chordXml(fingered(['C3', 'G3'], 4, 2, ['5', '1'])),
    chordXml(fingered(['E3'], 4, 2, ['3'])),
  ],
  [
    direction(wedge('crescendo'), 1, 'below'),
    chordXml(fingered(['C4', 'E4', 'G4'], 4, 1, ['1', '3', '5'])),
    direction(wedge('stop'), 1, 'below'),
    direction(dynamics('f'), 1, 'below'),
    chordXml(fingered(['D4', 'F4', 'A4', 'C5'], 4, 1, ['1', '2', '3', '5'])),
    BACKUP_A_BAR,
    chordXml(fingered(['F2', 'C3', 'A3'], 4, 2, ['5', '2', '1'])),
    direction(pedal('stop'), 2, 'below'),
    direction(dynamics('mf'), 2, 'below'),
    chordXml(fingered(['G2', 'D3', 'B3'], 4, 2, ['5', '2', '1'])),
  ],
]);

const LINE_2 = lineXml(3, [
  [
    direction(words('dolce'), 1, 'below'),
    direction(dynamics('sfz'), 1, 'below'),
    chordXml(plain(['E5'], 2, 1, articulations('staccato'))),
    chordXml(plain(['D5'], 2, 1, articulations('accent'))),
    chordXml(plain(['C5'], 2, 1, articulations('tenuto'))),
    chordXml(plain(['B4', 'D5'], 2, 1, articulations('staccato', 'accent'))),
    BACKUP_A_BAR,
    direction(pedal('start'), 2, 'below'),
    chordXml(plain(['G2'], 2, 2, articulations('staccato'))),
    chordXml(plain(['B2'], 2, 2, articulations('strong-accent'))),
    chordXml(plain(['D3'], 2, 2, articulations('tenuto'))),
    chordXml(plain(['G3'], 2, 2, articulations('staccatissimo'))),
  ],
  [
    direction(words('rit.'), 1, 'above'),
    direction(wedge('diminuendo'), 2, 'below'),
    chordXml(plain(['A4'], 4, 1, articulations('accent'))),
    chordXml(plain(['C5', 'E5'], 4, 1, FERMATA_ABOVE)),
    BACKUP_A_BAR,
    chordXml(plain(['C3'], 4, 2)),
    direction(wedge('stop'), 2, 'below'),
    direction(pedal('stop'), 2, 'below'),
    chordXml(plain(['A2', 'E3'], 4, 2, FERMATA_BELOW)),
  ],
]);

const LINE_3 = lineXml(5, [
  [
    direction(dynamics('ff'), 1, 'below'),
    direction(words('cresc. molto'), 1, 'below'),
    ...singleFingered(
      [
        ['A5', '1'],
        ['C6', '2'],
        ['E6', '4'],
      ],
      2,
      1,
    ),
    chordXml(fingered(['G6', 'C7'], 2, 1, ['1', '5'])),
    BACKUP_A_BAR,
    ...singleFingered(
      [
        ['E2', '5'],
        ['C2', '4'],
      ],
      2,
      2,
    ),
    chordXml(fingered(['A1', 'E2'], 4, 2, ['5', '1'])),
  ],
  [
    direction(dynamics('pp'), 2, 'below'),
    direction(words('calando'), 1, 'below'),
    chordXml(fingered(['C6', 'E6', 'G6'], 8, 1, ['1', '3', '5'], FERMATA_ABOVE)),
    BACKUP_A_BAR,
    chordXml(fingered(['C2', 'G2', 'C3'], 8, 2, ['5', '2', '1'], FERMATA_BELOW)),
    '<barline location="right"><bar-style>light-heavy</bar-style></barline>',
  ],
]);

/** One MusicXML document per line of the fixture song. */
export const REPERTOIRE_MARKINGS_LINES: string[] = [LINE_1, LINE_2, LINE_3];
